import React, { useState, useEffect, useRef } from "react";
import { authFetch } from "../services/httpClient";
import {
  Container,
  Typography,
  Button,
  Paper,
  Box,
  CircularProgress,
  Chip,
  Tooltip,
  Stack,
  TextField,
  MenuItem,
  Menu,
  IconButton,
  useTheme,
  Popper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemIcon,
  Autocomplete,
  Checkbox,
  Pagination,
} from "@mui/material";
import {
  Check as CheckIcon,
  KeyboardArrowDown as ArrowDownIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  // Schedule as ScheduleIcon,
  FileCopy as FileCopyIcon,
  Search as SearchIcon,
  Link as LinkIcon,
  SwapVert as SwapVertIcon,
  Person as PersonIcon,
  MoreVert as MoreVertIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import {
  getTemplates,
  deleteTemplate,
  generateTemplateLink,
  assignTemplate,
} from "../services/api";
import rbacApi from "../services/rbacApi";
import { getUser } from "../services/auth";
import { useNotification } from "../../interviewAI/NotificationContext";
import { format } from "date-fns";
import { useLocation } from "react-router-dom";
import { useRBAC } from "../context/RBACContext";
import { RESOURCE_DISPLAY_NAMES, ACTION_NAMES } from "../utils/resourceMapping";
import ConfirmationDialog from "./ConfirmationDialog";
import "./TemplateList.css";

const formatStatus = (status) => {
  if (!status) return "";
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const getUserDisplayName = (userId, users) => {
  if (!userId) return "Unknown";
  const user = users.find((u) => u.userId === userId);
  if (!user) return userId;

  const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  return fullName || user.email || user.userId;
};

const getCreatedDate = (value) => {
  if (!value) return null;
  let timestamp =
    typeof value === "string" ? value.trim().replace(" ", "T") : value;
  if (
    typeof timestamp === "string" &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(timestamp) &&
    !/(Z|[+-]\d{2}:?\d{2})$/i.test(timestamp)
  )
    timestamp += "Z";
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? null : date;
};
const statusColors = {
  ACTIVE: "#00a766",
  INACTIVE: "#8c939e",
  HOLD: "#eea721",
  CLOSED: "#f01455",
};

const getUserSearchText = (user) => {
  const fullName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
  return [user?.firstName, user?.lastName, fullName, user?.email, user?.userId]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

const TemplateList = () => {
  const location = useLocation();
  const templateRequestId = useRef(0);
  const [templates, setTemplates] = useState([]);
  const [fetchError, setFetchError] = useState("");
  const [managerFilter, setManagerFilter] = useState("");
  const [recruiterFilter, setRecruiterFilter] = useState("");
  const [page, setPage] = useState(1);
  // const [toolsAnchor, setToolsAnchor] = useState(null);
  const [statusAnchor, setStatusAnchor] = useState(null);
  const [statusTemplate, setStatusTemplate] = useState(null);
  const [users, setUsers] = useState([]);
  const [filterUsers, setFilterUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(
    location.state?.selectedUserId || "",
  );
  const [skipDefaultDateFilter, setSkipDefaultDateFilter] = useState(
    Boolean(location.state?.skipDefaultDateFilter),
  );
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterBy, setFilterBy] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [generatingLinkFor, setGeneratingLinkFor] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [templateToAssign, setTemplateToAssign] = useState(null);
  const [selectedAssignedUsers, setSelectedAssignedUsers] = useState([]);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [actionMenuTemplate, setActionMenuTemplate] = useState(null);
  const navigate = useNavigate();
  const { notify } = useNotification();

  const theme = useTheme();
  const { hasResourcePermissionByName } = useRBAC();

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [tempFromDate, setTempFromDate] = useState("");
  const [tempToDate, setTempToDate] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [dateRangeAnchorEl, setDateRangeAnchorEl] = useState(null);
  const dateRangeButtonRef = React.useRef(null);

  // Get current user for super admin check
  const currentUser = getUser();

  // Check if user is any Super Admin (regardless of scope)
  const isSuperAdmin = currentUser?.role?.name === "Super Admin";

  // Check permissions
  const canViewTemplates = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.VIEW_JOB_OPENING,
    ACTION_NAMES.GET_DATA,
  );
  const canCreateTemplate =
    hasResourcePermissionByName(
      RESOURCE_DISPLAY_NAMES.CREATE_JOB_OPENING,
      ACTION_NAMES.ADD_NEW,
    ) || isSuperAdmin;
  const canEditTemplate =
    hasResourcePermissionByName(
      RESOURCE_DISPLAY_NAMES.VIEW_JOB_OPENING,
      ACTION_NAMES.UPDATE,
    ) || isSuperAdmin;
  const canDeleteTemplate =
    hasResourcePermissionByName(
      RESOURCE_DISPLAY_NAMES.VIEW_JOB_OPENING,
      ACTION_NAMES.DELETE,
    ) || isSuperAdmin;
  const canScheduleInterview =
    hasResourcePermissionByName(
      RESOURCE_DISPLAY_NAMES.SCHEDULE_INTERVIEW,
      ACTION_NAMES.SCHEDULE,
    ) || isSuperAdmin;

  // Date Range and Sort Handlers
  const handleOpenDateDropdown = (event) => {
    setTempFromDate(fromDate);
    setTempToDate(toDate);
    setDateRangeAnchorEl(event.currentTarget);
  };

  const handleCloseDateDropdown = () => {
    setDateRangeAnchorEl(null);
  };

  const handleDateRangeApply = () => {
    setFromDate(tempFromDate);
    setToDate(tempToDate);
    setDateRangeAnchorEl(null);
  };

  const handleClearDateRange = () => {
    setTempFromDate("");
    setTempToDate("");
    setFromDate("");
    setToDate("");
    setDateRangeAnchorEl(null);
  };

  const toggleSortOrder = () => {
    const newOrder = sortOrder === "desc" ? "asc" : "desc";
    setSortOrder(newOrder);
  };

  // Fetch templates function with date range and sort support
  const fetchTemplates = async (
    from = fromDate,
    to = toDate,
    order = sortOrder,
    userId = selectedUserId,
  ) => {
    const requestId = ++templateRequestId.current;
    try {
      setLoading(true);
      setFetchError("");

      const response = await getTemplates(
        from || undefined,
        to || undefined,
        order,
        userId || undefined,
        skipDefaultDateFilter && !from && !to,
      );

      if (requestId !== templateRequestId.current) return;
      setTemplates(response.data || []);

      if (response.defaultRange) {
        setFromDate(response.defaultRange.fromDate);
        setToDate(response.defaultRange.toDate);

        setTempFromDate(response.defaultRange.fromDate);
        setTempToDate(response.defaultRange.toDate);
      }

      setLoading(false);
    } catch (error) {
      if (requestId !== templateRequestId.current) return;
      setFetchError(error?.message || "Job openings could not be loaded.");
      console.error("Error fetching Job Templates:", error);
      setTemplates([]);
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await rbacApi.get("/user_crud/users");
      setUsers(res.data);
    } catch (err) {
      // If we can't fetch users (403), just set users to empty array and continue
      console.warn("Couldn't fetch users (no permission or error):", err);
      setUsers([]);
    }
  };

  const fetchFilterUsers = async () => {
    try {
      const res = await rbacApi.get("/templates/filter-users");
      setFilterUsers(res.data || []);
    } catch (err) {
      console.warn("Couldn't fetch Job Opening filter users:", err);
      setFilterUsers([]);
    }
  };

  const handleAssignClick = (template) => {
    const explicitAssignees = (template.assigned_to || []).filter(
      (userId) => userId !== template.created_by,
    );

    setTemplateToAssign(template);
    setSelectedAssignedUsers(explicitAssignees);
    setAssignDialogOpen(true);
  };

  const handleActionMenuOpen = (event, template) => {
    setActionMenuAnchorEl(event.currentTarget);
    setActionMenuTemplate(template);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null);
    setActionMenuTemplate(null);
  };

  const handleConfirmAssign = async () => {
    try {
      const updated = await assignTemplate(
        templateToAssign._id,
        selectedAssignedUsers,
      );
      setTemplates((prev) =>
        prev.map((t) => (t._id === updated._id ? updated : t)),
      );
      notify("success", "Template assigned successfully!");
    } catch (err) {
      console.error("Error assigning template:", err);
      notify("error", err?.message || "Failed to assign template");
    } finally {
      setAssignDialogOpen(false);
      setTemplateToAssign(null);
      setSelectedAssignedUsers([]);
    }
  };

  const handleRemoveAssignedUser = (userId) => {
    setSelectedAssignedUsers((prev) => prev.filter((id) => id !== userId));
  };

  const filteredUserOptions = (options, { inputValue }) => {
    const term = inputValue.trim().toLowerCase();

    if (!term) {
      return options;
    }

    return options.filter((user) => getUserSearchText(user).includes(term));
  };

  useEffect(() => {
    if (
      Object.prototype.hasOwnProperty.call(
        location.state || {},
        "selectedUserId",
      )
    )
      setSelectedUserId(location.state.selectedUserId || "");
    setSkipDefaultDateFilter(Boolean(location.state?.skipDefaultDateFilter));
    if (location.state?.skipDefaultDateFilter) {
      setSkipDefaultDateFilter(true);
      setFromDate("");
      setToDate("");
      setTempFromDate("");
      setTempToDate("");
    }
    if (location.state?.filterBy === "Status") {
      const { filterStatus } = location.state;
      if (filterStatus) {
        setFilterBy("Status");
        setSearchTerm(filterStatus.toLowerCase());
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    fetchTemplates(fromDate, toDate, sortOrder, selectedUserId);
    fetchUsers();
    fetchFilterUsers();
  }, [sortOrder, fromDate, toDate, selectedUserId, skipDefaultDateFilter]);

  const handleDeleteClick = (id) => {
    setTemplateToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (templateToDelete) {
      try {
        await deleteTemplate(templateToDelete);
        notify("success", "Job opening deleted successfully");
        fetchTemplates();
      } catch (error) {
        // Extract the error message - try multiple paths
        let errorMessage = error?.message;
        if (!errorMessage && error?.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        }
        errorMessage = errorMessage || "Failed to delete job opening";
        console.error("Error deleting Job Opening:", error);
        console.error("Error message being shown:", errorMessage);
        notify("error", errorMessage);
      }
    }
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };

  const handleGenerateAndCopyLink = async (templateId) => {
    try {
      setGeneratingLinkFor(templateId);
      const response = await generateTemplateLink(templateId);
      const { generatedLink } = response;

      if (generatedLink) {
        await navigator.clipboard.writeText(generatedLink);
        // Open the link in a new tab
        window.open(generatedLink, "_blank", "noopener,noreferrer");
        notify("success", "Link copied to clipboard and opened in new tab!");
      } else {
        notify("error", "Failed to generate link");
      }
    } catch (error) {
      console.error("Error generating template link:", error);
      notify("error", error.message || "Failed to generate link");
    } finally {
      setGeneratingLinkFor(null);
    }
  };

  const allUsers = [
    ...filterUsers,
    ...users.filter(
      (user) => !filterUsers.some((option) => option.userId === user.userId),
    ),
  ];
  const creatorOf = (template) =>
    allUsers.find((user) => user.userId === template.created_by);
  const managerIdOf = (template) =>
    template.manager_id ||
    template.managerId ||
    creatorOf(template)?.manager_id ||
    creatorOf(template)?.managerId ||
    "";
  const managerNameOf = (template) =>
    template.managerName ||
    (managerIdOf(template)
      ? getUserDisplayName(managerIdOf(template), allUsers)
      : "\u2014");
  const recruiterNameOf = (template) =>
    template.createdByName ||
    (template.created_by
      ? getUserDisplayName(template.created_by, allUsers)
      : "\u2014");
  // Use the API's authorized reporting tree, independently of loaded jobs.
  const hierarchyUsers = new Map(filterUsers.map(user => [user.userId, user]));
  const reportingManagerIds = new Set(
    filterUsers.map(user => user.manager_id || user.managerId).filter(Boolean),
  );
  const isInBranch = (userId, managerId) => {
    const visited = new Set();
    let user = hierarchyUsers.get(userId);
    while (user && !visited.has(user.userId)) {
      if (user.userId === managerId) return true;
      if (Array.isArray(user.ancestorIds) && user.ancestorIds.includes(managerId)) return true;
      visited.add(user.userId);
      user = hierarchyUsers.get(user.manager_id || user.managerId);
    }
    return false;
  };
  const branchUsers = filterUsers.filter(user =>
    !selectedUserId || isInBranch(user.userId, selectedUserId),
  );
  const roleOf = user => String(
    user.roleName || user.role?.name ||
    users.find(option => option.userId === user.userId)?.role?.name || "",
  ).toLowerCase().replace(/[\s_-]/g, "");
  const managers = branchUsers.filter(user =>
    user.filterType === "manager" ||
    (user.filterType === undefined && roleOf(user) !== "superadmin" &&
      (roleOf(user).includes("manager") || roleOf(user) === "admin" ||
        reportingManagerIds.has(user.userId))),
  );
  const recruiters = branchUsers.filter(user =>
    (user.filterType === "recruiter" ||
      (user.filterType === undefined &&
        (roleOf(user) === "recruiter" ||
          (roleOf(user) === "user" && (user.manager_id || user.managerId) &&
            !reportingManagerIds.has(user.userId))))) &&
    (!managerFilter || isInBranch(user.userId, managerFilter)),
  );
  useEffect(() => {
    setManagerFilter("");
    setRecruiterFilter("");
  }, [selectedUserId]);
  useEffect(() => {
    setPage(1);
  }, [
    searchTerm,
    filterBy,
    statusFilter,
    managerFilter,
    recruiterFilter,
    selectedUserId,
    fromDate,
    toDate,
  ]);
  const filteredTemplates = templates.filter((template) => {
    if (managerFilter && !isInBranch(template.created_by, managerFilter) &&
      !isInBranch(managerIdOf(template), managerFilter)) return false;
    if (recruiterFilter && template.created_by !== recruiterFilter)
      return false;
    // Status filter from table filter icon
    if (statusFilter !== "All") {
      const templateStatus = (
        template.templateStatus || "ACTIVE"
      ).toUpperCase();

      if (templateStatus !== statusFilter.toUpperCase()) {
        return false;
      }
    }

    const term = searchTerm?.trim().toLowerCase();

    // No search term
    if (!term) return true;

    // Job Opening
    const jobOpeningMatch = template.name?.toLowerCase().includes(term);

    // Status
    const status = (template.templateStatus || "ACTIVE").toLowerCase();
    const statusMatch = status.includes(term);

    // Req ID
    const reqIdMatch = template.reqId?.toString().toLowerCase().includes(term);

    // Created By
    const createdByUser = users.find(
      (user) => user.userId === template.created_by,
    );

    const createdByName = recruiterNameOf(template);

    const createdByMatch =
      createdByName.toLowerCase().includes(term) ||
      createdByUser?.email?.toLowerCase().includes(term) ||
      createdByUser?.userId?.toLowerCase().includes(term);

    switch (filterBy) {
      case "Name":
        return jobOpeningMatch;

      case "Status":
        return statusMatch;

      case "Req ID":
        return reqIdMatch;

      case "Created By":
        return createdByMatch;

      case "All":
      default:
        return jobOpeningMatch || statusMatch || reqIdMatch || createdByMatch;
    }
  });

  const handleStatusChange = async (id, newStatus) => {
    try {
      setTemplates((prev) =>
        prev.map((t) =>
          t._id === id ? { ...t, templateStatus: newStatus } : t,
        ),
      );

      const BASE_URL = process.env.REACT_APP_TEMPLATE_BASE_URL;

      await authFetch(`${BASE_URL}/templates/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ templateStatus: newStatus }),
      });

      console.log(`Template ${id} status updated to ${newStatus}`);
      notify("success", `Status updated to ${newStatus}`);
    } catch (error) {
      const errorMessage = error?.message || "Failed to update status";
      console.error("Error updating status:", error);
      notify("error", errorMessage);
      // Revert the optimistic update
      fetchTemplates();
    }
  };

  if (!canViewTemplates) {
    return (
      <Container
        maxWidth={false}
        disableGutters
        sx={{ mt: 8, textAlign: "center", py: 6 }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, color: "#1976d2", mb: 2 }}
        >
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You don't have access to view job openings. Contact your
          administrator.
        </Typography>
      </Container>
    );
  }

  const pageCount = Math.max(1, Math.ceil(filteredTemplates.length / 10));
  const currentPage = Math.min(page, pageCount);
  const visibleTemplates = filteredTemplates.slice(
    (currentPage - 1) * 10,
    currentPage * 10,
  );
  const resetFilters = () => {
    setSearchTerm("");
    setFilterBy("All");
    setStatusFilter("All");
    setManagerFilter("");
    setRecruiterFilter("");
    setFromDate("");
    setToDate("");
    setTempFromDate("");
    setTempToDate("");
    setSkipDefaultDateFilter(true);
  };
  return (
    <Container maxWidth={false} disableGutters className="tt-jobs-page">
      <main className="tt-jobs-main">
        <header className="tt-jobs-heading">
          <div>
            <h1>Job Openings</h1>
            <p>Manage and customize your job openings descriptions</p>
          </div>
          <div className="tt-jobs-heading-actions">
            <label className="tt-jobs-sr-only" htmlFor="tt-jobs-team">
              Filter by team
            </label>
            <select
              id="tt-jobs-team"
              value={selectedUserId}
              onChange={(event) => {
                setSelectedUserId(event.target.value);
                setManagerFilter("");
                setRecruiterFilter("");
                setPage(1);
              }}
            >
              <option value="">All teams</option>
              {filterUsers.map((user) => (
                <option key={user.userId} value={user.userId}>
                  {`${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                    user.email ||
                    user.userId}
                  's team
                </option>
              ))}
            </select>
            <Tooltip
              title={
                !canCreateTemplate
                  ? "You don't have permission to create job openings"
                  : "Create a job opening"
              }
            >
              <span>
                <Button
                  className="tt-jobs-primary"
                  variant="contained"
                  startIcon={<AddIcon />}
                  component={canCreateTemplate ? Link : "button"}
                  to={canCreateTemplate ? "/templates/new" : undefined}
                  disabled={!canCreateTemplate}
                >
                  Create New Job
                </Button>
              </span>
            </Tooltip>
          </div>
        </header>
        <section
          className="tt-jobs-filters"
          aria-labelledby="tt-jobs-filters-title"
        >
          <h2 id="tt-jobs-filters-title">Filters</h2>
          <div className="tt-jobs-filter-grid">
            <label>
              Managers
              <select
                value={managerFilter}
                onChange={(event) => {
                  setManagerFilter(event.target.value);
                  setRecruiterFilter("");
                }}
              >
                <option value="">All Managers</option>
                {managers.map(user => (
                  <option key={user.userId} value={user.userId}>
                    {getUserDisplayName(user.userId, filterUsers)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Recruiter
              <select
                value={recruiterFilter}
                onChange={(event) => setRecruiterFilter(event.target.value)}
              >
                <option value="">All Recruiter</option>
                {recruiters.map(user => (
                  <option key={user.userId} value={user.userId}>
                    {getUserDisplayName(user.userId, filterUsers)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Status
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value="All">Status</option>
                {Object.keys(statusColors).map((status) => (
                  <option key={status} value={status}>
                    {formatStatus(status)}
                  </option>
                ))}
              </select>
            </label>
            <div className="tt-jobs-date-field">
              <label>Date Range</label>
              <button
                ref={dateRangeButtonRef}
                onClick={handleOpenDateDropdown}
                title={
                  fromDate && toDate
                    ? `${fromDate} to ${toDate}`
                    : "Select Date Range"
                }
              >
                <span>
                  {fromDate && toDate
                    ? `${format(new Date(fromDate), "dd/MM/yy")} \u2013 ${format(new Date(toDate), "dd/MM/yy")}`
                    : "Select Date Range"}
                </span>
                <CalendarIcon />
              </button>
            </div>
            <label className="tt-jobs-search">
              <span className="tt-jobs-sr-only">Search job openings</span>
              <SearchIcon />
              <input
                placeholder="Search by Job Opening"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </label>
            <div className="tt-jobs-filter-buttons">
              <Button variant="outlined" onClick={resetFilters}>
                Reset
              </Button>
              <Button
                className="tt-jobs-primary"
                variant="contained"
                onClick={() => fetchTemplates()}
              >
                Apply
              </Button>
            </div>
          </div>
        </section>
        <Popper
          open={Boolean(dateRangeAnchorEl)}
          anchorEl={dateRangeAnchorEl}
          placement="bottom-start"
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
            {
              name: "flip",
              options: {
                padding: 8,
              },
            },
          ]}
          style={{ zIndex: 1300 }}
        >
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 2,
              boxShadow: "0 12px 32px rgba(0, 0, 0, 0.15)",
              minWidth: 320,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                fontSize: "0.95rem",
                mb: 2,
                color: theme.palette.primary.main,
              }}
            >
              📅 Select date range
            </Typography>

            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  mb: 1,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Start date
              </Typography>
              <TextField
                type="date"
                value={tempFromDate}
                onChange={(e) => setTempFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                variant="outlined"
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    backgroundColor: "var(--bg-input)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "var(--bg-input)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "transparent",
                      boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
                      borderColor: theme.palette.primary.main,
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--border-color)",
                  },
                }}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  mb: 1,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                End date
              </Typography>
              <TextField
                type="date"
                value={tempToDate}
                onChange={(e) => setTempToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    backgroundColor: "var(--bg-input)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "var(--bg-input)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "transparent",
                      boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
                      borderColor: theme.palette.primary.main,
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--border-color)",
                  },
                }}
              />
            </Box>

            {tempFromDate && tempToDate && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  backgroundColor: theme.palette.primary.light,
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderRadius: 1.5,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    fontSize: "0.9rem",
                  }}
                >
                  {tempFromDate} → {tempToDate}
                </Typography>
              </Box>
            )}

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                onClick={handleCloseDateDropdown}
                variant="text"
                size="small"
                sx={{
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "0.9rem",
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleClearDateRange}
                variant="outlined"
                size="small"
                color="error"
                sx={{
                  fontWeight: 700,
                  textTransform: "none",
                  fontSize: "0.9rem",
                  borderRadius: 1,
                }}
              >
                Clear
              </Button>
              <Button
                onClick={handleDateRangeApply}
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: "#4c13a5",
                  fontWeight: 700,
                  textTransform: "none",
                  fontSize: "0.9rem",
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: "#3d0d8b",
                    boxShadow: "none",
                  },
                }}
              >
                Apply
              </Button>
            </Stack>
          </Paper>
        </Popper>
        <section
          className="tt-jobs-card"
          aria-labelledby="tt-jobs-table-title"
          aria-busy={loading}
        >
          <div className="tt-jobs-card-heading">
            <h2 id="tt-jobs-table-title">Jobs</h2>
            {/* List options hidden by request.
            <Tooltip title="More list options">
              <IconButton
                aria-label="More list options"
                size="small"
                onClick={(event) => setToolsAnchor(event.currentTarget)}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            */}
          </div>
          <div className="tt-jobs-table-scroll">
            <table className="tt-jobs-table">
              <thead>
                <tr>
                  <th scope="col">
                    <button
                      className="tt-jobs-sort"
                      onClick={toggleSortOrder}
                      title={`Sort ${sortOrder === "desc" ? "oldest" : "newest"} first`}
                    >
                      Date
                      <SwapVertIcon />
                    </button>
                  </th>
                  {[
                    "Time",
                    "Req ID",
                    "Manager",
                    "Recruiter",
                    "Title",
                    "Schedule",
                    "Schedule",
                    "Status",
                    "Action",
                  ].map((label, index) => (
                    <th scope="col" key={index}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10} className="tt-jobs-empty">
                      <CircularProgress size={24} />
                      <p>Loading your Job Openings...</p>
                    </td>
                  </tr>
                ) : fetchError ? (
                  <tr>
                    <td colSpan={10} className="tt-jobs-empty">
                      <p role="alert">{fetchError}</p>
                      <Button onClick={() => fetchTemplates()}>Retry</Button>
                    </td>
                  </tr>
                ) : !visibleTemplates.length ? (
                  <tr>
                    <td colSpan={10} className="tt-jobs-empty">
                      <strong>No Job Openings found.</strong>
                      <p>Try adjusting your search or filter options.</p>
                    </td>
                  </tr>
                ) : (
                  visibleTemplates.map((template) => {
                    const created = getCreatedDate(template.createdAt);
                    const status = (
                      template.templateStatus || "ACTIVE"
                    ).toUpperCase();
                    return (
                      <tr key={template._id}>
                        <td>
                          {created ? format(created, "dd/MM/yyyy") : "\u2014"}
                        </td>
                        <td>
                          {created
                            ? format(created, "h:mm a").toLowerCase()
                            : "\u2014"}
                        </td>
                        <td>{template.reqId || "\u2014"}</td>
                        <td>
                          <span
                            className="tt-jobs-truncate"
                            title={managerNameOf(template)}
                          >
                            {allUsers.find(user => user.userId === managerIdOf(template))?.firstName || managerNameOf(template).trim().split(/\s+/)[0]}
                          </span>
                        </td>
                        <td>
                          <span
                            className="tt-jobs-truncate"
                            title={recruiterNameOf(template)}
                          >
                            {creatorOf(template)?.firstName || recruiterNameOf(template).trim().split(/\s+/)[0]}
                          </span>
                        </td>
                        <td>
                          <Tooltip title={template.name || "\u2014"}>
                            <span className="tt-jobs-title tt-jobs-truncate">
                              {template.name || "\u2014"}
                            </span>
                          </Tooltip>
                        </td>
                        <td>
                          <Tooltip
                            title={
                              canScheduleInterview
                                ? "Schedule Interview"
                                : "You don't have permission to schedule interviews"
                            }
                          >
                            <span>
                              <Button
                                className="tt-jobs-schedule"
                                component={
                                  canScheduleInterview ? Link : "button"
                                }
                                to={
                                  canScheduleInterview
                                    ? `/interviews/new?templateId=${template._id}&from=job-openings`
                                    : undefined
                                }
                                disabled={!canScheduleInterview}
                                variant="outlined"
                              >
                                Schedule
                              </Button>
                            </span>
                          </Tooltip>
                        </td>
                        <td>
                          <button
                            className="tt-jobs-count"
                            aria-label={`${template.interviewCount || 0} scheduled interviews for ${template.name || "job opening"}`}
                            disabled={!(template.interviewCount > 0)}
                            onClick={() =>
                              navigate("/interviews", {
                                state: {
                                  filterBy: "Job Template",
                                  filterStatus: template.name,
                                  templateId: template._id,
                                  skipDefaultDateFilter: true,
                                },
                              })
                            }
                          >
                            {template.interviewCount || 0}
                          </button>
                        </td>
                        <td>
                          <button
                            className={`tt-jobs-status tt-jobs-status-${status.toLowerCase()}`}
                            aria-label={`Change status for ${template.name || "job opening"}, currently ${formatStatus(status)}`}
                            aria-haspopup="menu"
                            onClick={(event) => {
                              setStatusAnchor(event.currentTarget);
                              setStatusTemplate(template);
                            }}
                          >
                            {formatStatus(status)}
                            <ArrowDownIcon />
                          </button>
                        </td>
                        <td>
                          <Tooltip title="More actions">
                            <IconButton
                              aria-label={`Actions for ${template.name || "job opening"}`}
                              size="small"
                              onClick={(event) =>
                                handleActionMenuOpen(event, template)
                              }
                            >
                              <MoreVertIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <footer className="tt-jobs-footer">
            <span>
              Showing {loading ? 0 : visibleTemplates.length} of{" "}
              {filteredTemplates.length} results
            </span>
            <Pagination
              count={pageCount}
              page={currentPage}
              onChange={(_, next) => setPage(next)}
              shape="rounded"
              size="small"
              siblingCount={1}
              boundaryCount={1}
            />
          </footer>
        </section>
      </main>
      {/* List options menu retained for future use.
      <Menu
        anchorEl={toolsAnchor}
        open={Boolean(toolsAnchor)}
        onClose={() => setToolsAnchor(null)}
      >
        <MenuItem
          component={canScheduleInterview ? Link : "li"}
          to={
            canScheduleInterview
              ? "/interviews/new?from=job-openings"
              : undefined
          }
          disabled={!canScheduleInterview}
          onClick={() => setToolsAnchor(null)}
        >
          <ListItemIcon>
            <ScheduleIcon fontSize="small" />
          </ListItemIcon>
          Schedule Interview
        </MenuItem>
        <Box sx={{ px: 2, py: 1 }}>
          <TextField
            select
            size="small"
            fullWidth
            label="Search In"
            value={filterBy}
            onChange={(event) => setFilterBy(event.target.value)}
          >
            {["All", "Name", "Status", "Req ID", "Created By"].map((value) => (
              <MenuItem key={value} value={value}>
                {value === "Name" ? "Job Opening" : value}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <MenuItem
          onClick={() => {
            toggleSortOrder();
            setToolsAnchor(null);
          }}
        >
          <ListItemIcon>
            <SwapVertIcon fontSize="small" />
          </ListItemIcon>
          Sort {sortOrder === "desc" ? "oldest" : "newest"} first
        </MenuItem>
      </Menu>
      */}
      <Menu
        anchorEl={statusAnchor}
        open={Boolean(statusAnchor)}
        onClose={() => setStatusAnchor(null)}
        PaperProps={{ className: "tt-jobs-status-menu" }}
      >
        {Object.entries(statusColors).map(([status, color]) => (
          <MenuItem
            key={status}
            selected={
              (statusTemplate?.templateStatus || "ACTIVE").toUpperCase() ===
              status
            }
            onClick={() => {
              handleStatusChange(statusTemplate._id, status);
              setStatusAnchor(null);
            }}
          >
            <span
              className="tt-jobs-status-dot"
              style={{ background: color }}
            />
            <span style={{ color, flex: 1 }}>{status}</span>
            {(statusTemplate?.templateStatus || "ACTIVE").toUpperCase() ===
              status && <CheckIcon sx={{ fontSize: 15, color: "#681dde" }} />}
          </MenuItem>
        ))}
      </Menu>
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Job Opening"
        message="Are you sure you want to delete this Job Opening? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous={true}
      />

      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={handleActionMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{ className: "tt-jobs-action-menu" }}
      >
        {(actionMenuTemplate?.assigned_to || []).some(
          (userId) => userId !== actionMenuTemplate?.created_by,
        ) && (
          <Box className="tt-jobs-action-menu-assigned">
            <Typography variant="caption" sx={{ color: "#647694" }}>
              Assigned to
            </Typography>
            {(actionMenuTemplate.assigned_to || [])
              .filter((userId) => userId !== actionMenuTemplate.created_by)
              .map((userId) => (
                <Typography key={userId} variant="body2" sx={{ py: 0.25 }}>
                  {getUserDisplayName(userId, allUsers)}
                </Typography>
              ))}
          </Box>
        )}
        <MenuItem
          onClick={() => {
            handleActionMenuClose();
            if (actionMenuTemplate) {
              handleAssignClick(actionMenuTemplate);
            }
          }}
          disabled={!canEditTemplate}
        >
          <ListItemIcon sx={{ color: "#1976d2" }}>
            <PersonIcon />
          </ListItemIcon>
          Assign Users
        </MenuItem>
        <MenuItem
          disabled={Boolean(generatingLinkFor)}
          onClick={() => {
            handleActionMenuClose();
            handleGenerateAndCopyLink(actionMenuTemplate?._id);
          }}
        >
          <ListItemIcon sx={{ color: "#00bcd4" }}>
            <LinkIcon />
          </ListItemIcon>
          {generatingLinkFor === actionMenuTemplate?._id
            ? "Generating link..."
            : "Copy Link"}
        </MenuItem>
        <MenuItem
          component={Link}
          to={
            canEditTemplate && actionMenuTemplate
              ? `/templates/edit/${actionMenuTemplate._id}`
              : undefined
          }
          onClick={handleActionMenuClose}
          disabled={!canEditTemplate}
        >
          <ListItemIcon sx={{ color: "#4caf50" }}>
            <EditIcon />
          </ListItemIcon>
          Edit Job Opening
        </MenuItem>
        <MenuItem
          component={Link}
          to={
            canCreateTemplate && actionMenuTemplate
              ? `/templates/clone/${actionMenuTemplate._id}`
              : undefined
          }
          onClick={handleActionMenuClose}
          disabled={!canCreateTemplate}
        >
          <ListItemIcon sx={{ color: "#9c27b0" }}>
            <FileCopyIcon />
          </ListItemIcon>
          Clone Job Opening
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleActionMenuClose();
            if (canDeleteTemplate && actionMenuTemplate) {
              handleDeleteClick(actionMenuTemplate._id);
            }
          }}
          disabled={!canDeleteTemplate}
        >
          <ListItemIcon sx={{ color: "#e64067" }}>
            <DeleteIcon />
          </ListItemIcon>
          Delete Job Opening
        </MenuItem>
      </Menu>

      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Job Opening</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            disableCloseOnSelect
            fullWidth
            sx={{ mt: 2 }}
            options={users}
            value={users.filter((user) =>
              selectedAssignedUsers.includes(user.userId),
            )}
            onChange={(_, selectedUsers) => {
              setSelectedAssignedUsers(
                selectedUsers.map((user) => user.userId),
              );
            }}
            filterOptions={filteredUserOptions}
            getOptionLabel={(user) => {
              const fullName =
                `${user.firstName || ""} ${user.lastName || ""}`.trim();
              return fullName || user.email || user.userId;
            }}
            isOptionEqualToValue={(option, value) =>
              option.userId === value.userId
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                <Box
                  sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {`${option.firstName || ""} ${option.lastName || ""}`.trim() ||
                      option.email ||
                      option.userId}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email || "No email available"}
                  </Typography>
                </Box>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((user, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={user.userId}
                  label={getUserDisplayName(user.userId, users)}
                  onDelete={() => handleRemoveAssignedUser(user.userId)}
                  size="small"
                  sx={{
                    margin: 0.5,
                    borderRadius: "999px",
                    backgroundColor: "#E0F2FE",
                    color: "#0369A1",
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Users"
                placeholder="Search by first name, last name, or email"
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmAssign} variant="contained">
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TemplateList;
